/**
 * Iter 217+230 — Hub Marketing centralisé.
 * Route : /admin/marketing
 *
 * Vue d'ensemble unifiée :
 *  - Dashboard "Automations en cours" (Iter 230) — KPIs temps réel
 *  - KPIs principaux (audiences, ROAS, taux d'ouverture emails, posts publiés)
 *  - Raccourcis vers chaque sous-module
 *  - Suggestions IA / actions recommandées
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import { Loader2, Megaphone, Mail, Globe, TrendingUp, Sparkles, ChevronRight, Send, Users, Target, BarChart3, Calendar, Plus, Workflow, MessageCircle, Zap } from "lucide-react";
import PageHeader from "../components/PageHeader";
import AutomationsDashboard from "../components/marketing/AutomationsDashboard";

const MODULES = [
  {
    id: "automations",
    title: "Workflows & Automations",
    desc: "Triggers, actions IA, branches conditionnelles, dunning Stripe — façon GoHighLevel",
    href: "/admin/workflows",
    icon: Workflow,
    color: "#a855f7",
    badge: "Iter 222",
  },
  {
    id: "messagerie",
    title: "Messagerie Unifiée",
    desc: "WhatsApp · Messenger · Instagram avec agent IA mixte (auto/suggestion/manuel)",
    href: "/admin/messagerie",
    icon: MessageCircle,
    color: "#25d366",
    badge: "Iter 220",
  },
  {
    id: "social",
    title: "Réseaux Sociaux",
    desc: "Posts Instagram, Facebook, LinkedIn, X — planification + insights",
    href: "/admin/marketing/social",
    icon: Megaphone,
    color: "#8b5cf6",
  },
  {
    id: "studio",
    title: "Studio éditorial IA",
    desc: "Médiathèque photos/vidéos, consignes & cadence, calendrier auto (posts, stories, mailings)",
    href: "/admin/marketing/studio",
    icon: Sparkles,
    color: "#f7c935",
  },
  {
    id: "mailing",
    title: "Email Marketing",
    desc: "Campagnes email, segmentation, automations transactionnelles",
    href: "/admin/marketing/mailing",
    icon: Mail,
    color: "#3b82f6",
  },
  {
    id: "google",
    title: "Google Ads & Search",
    desc: "Search Console, Analytics, audiences publicitaires Google",
    href: "/admin/marketing/google",
    icon: Globe,
    color: "#22c55e",
  },
  {
    id: "audiences",
    title: "Lead Gen IA",
    desc: "Génération d'audiences et personas par IA + tracking ROAS",
    href: "/admin/marketing/audiences",
    icon: Target,
    color: "#f43f5e",
  },
];

export default function MarketingHubPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [stats, setStats] = useState({
    audiences: { count: 0, total_roas: 0, total_spent_eur: 0, total_revenue_eur: 0 },
    posts: { total: 0, last_30d: 0 },
    emails: { total: 0, last_30d: 0, avg_open_rate: 0 },
    campaigns: { total: 0, active: 0 },
  });
  const [recent, setRecent] = useState({ audiences: [], posts: [], emails: [] });
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const promises = [
        api.get("/google/audiences").catch(() => ({ data: { audiences: [] } })),
        api.get("/social/posts?limit=5").catch(() => ({ data: { posts: [] } })),
        api.get("/mailing/campaigns?limit=5").catch(() => ({ data: { campaigns: [] } })),
      ];
      const [aud, soc, mai] = await Promise.all(promises);
      const audiences = aud.data?.audiences || aud.data?.items || [];
      const posts = soc.data?.posts || soc.data?.items || [];
      const emails = mai.data?.campaigns || mai.data?.items || [];

      // Aggregate audience metrics
      let total_roas = 0;
      let total_spent_eur = 0;
      let total_revenue_eur = 0;
      audiences.forEach((a) => {
        total_spent_eur += parseFloat(a.spent_eur || a.total_spent_eur || 0);
        total_revenue_eur += parseFloat(a.revenue_eur || a.total_revenue_eur || 0);
      });
      total_roas = total_spent_eur > 0 ? (total_revenue_eur / total_spent_eur) : 0;

      // Email opens average
      let avg_open = 0;
      if (emails.length > 0) {
        const opens = emails.map((e) => parseFloat(e.open_rate || e.stats?.open_rate || 0));
        avg_open = opens.reduce((a, b) => a + b, 0) / opens.length;
      }

      setStats({
        audiences: { count: audiences.length, total_roas, total_spent_eur, total_revenue_eur },
        posts: { total: posts.length, last_30d: posts.length },
        emails: { total: emails.length, last_30d: emails.length, avg_open_rate: avg_open },
        campaigns: { total: emails.length, active: emails.filter((e) => (e.status || "") === "sent").length },
      });
      setRecent({ audiences: audiences.slice(0, 3), posts: posts.slice(0, 3), emails: emails.slice(0, 3) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="marketing-hub">
      <PageHeader
        title="Hub Marketing"
        subtitle="Vue d'ensemble de vos campagnes, audiences, posts et performances. Tout votre marketing au même endroit."
        icon={<Megaphone />}
      />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
        </div>
      ) : (
        <>
          {/* Iter 230 — Dashboard Automations en tête de page */}
          <div className="mb-6">
            <AutomationsDashboard />
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="marketing-kpis">
            <KpiCard
              label="Audiences IA actives"
              value={stats.audiences.count}
              icon={<Target className="w-4 h-4" />}
              accent="#f43f5e"
              testid="kpi-audiences"
            />
            <KpiCard
              label="ROAS global"
              value={stats.audiences.total_roas > 0 ? `×${stats.audiences.total_roas.toFixed(2)}` : "—"}
              sub={stats.audiences.total_revenue_eur > 0 ? `${Math.round(stats.audiences.total_revenue_eur)}€ revenus / ${Math.round(stats.audiences.total_spent_eur)}€ dépensés` : "Aucune donnée"}
              icon={<TrendingUp className="w-4 h-4" />}
              accent="#22c55e"
              testid="kpi-roas"
            />
            <KpiCard
              label="Campagnes email"
              value={stats.campaigns.total}
              sub={`${stats.campaigns.active} envoyées · ${stats.emails.avg_open_rate ? `${(stats.emails.avg_open_rate * 100).toFixed(1)}% ouv. moyen` : "—"}`}
              icon={<Mail className="w-4 h-4" />}
              accent="#3b82f6"
              testid="kpi-emails"
            />
            <KpiCard
              label="Posts sociaux"
              value={stats.posts.total}
              sub="30 derniers jours"
              icon={<Megaphone className="w-4 h-4" />}
              accent="#8b5cf6"
              testid="kpi-posts"
            />
          </div>

          {/* Modules */}
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">Modules</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            {MODULES.map((m) => {
              const Icon = m.icon;
              return (
                <Link key={m.id} to={m.href} className="group bg-[#0b0811] hover:bg-[#14101c] border border-white/5 hover:border-white/15 rounded-xl p-4 transition-all flex items-start gap-3" data-testid={`module-${m.id}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: m.color + "22" }}>
                    <Icon className="w-5 h-5" style={{ color: m.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-sm font-bold text-white">{m.title}</h3>
                      <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-white group-hover:translate-x-1 transition-all" />
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">{m.desc}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Recent activity */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Audiences */}
            <RecentSection title="Dernières audiences IA" icon={Target} color="#f43f5e" href="/admin/marketing/audiences">
              {recent.audiences.length === 0 ? (
                <EmptyState text="Pas encore d'audience générée." cta="Créer ma 1ère audience" href="/admin/marketing/audiences" />
              ) : (
                recent.audiences.map((a) => (
                  <div key={a.audience_id} className="p-2.5 bg-[#14101c] rounded-lg" data-testid={`audience-${a.audience_id}`}>
                    <div className="text-xs text-white font-bold truncate">{a.persona_name || a.title || "Audience"}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {a.spent_eur ? `${a.spent_eur}€ dépensés` : "Pas encore lancée"}
                    </div>
                  </div>
                ))
              )}
            </RecentSection>

            {/* Posts */}
            <RecentSection title="Derniers posts sociaux" icon={Megaphone} color="#8b5cf6" href="/admin/marketing/social">
              {recent.posts.length === 0 ? (
                <EmptyState text="Aucun post publié récemment." cta="Programmer un post" href="/admin/marketing/social" />
              ) : (
                recent.posts.map((p) => (
                  <div key={p.post_id || p.id} className="p-2.5 bg-[#14101c] rounded-lg" data-testid={`post-${p.post_id || p.id}`}>
                    <div className="text-xs text-white truncate">{p.title || p.content?.slice(0, 40) || "Post"}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{p.platform || "—"} · {p.scheduled_at?.slice(0, 10) || p.created_at?.slice(0, 10)}</div>
                  </div>
                ))
              )}
            </RecentSection>

            {/* Emails */}
            <RecentSection title="Dernières campagnes email" icon={Mail} color="#3b82f6" href="/admin/marketing/mailing">
              {recent.emails.length === 0 ? (
                <EmptyState text="Aucune campagne envoyée." cta="Créer une campagne" href="/admin/marketing/mailing" />
              ) : (
                recent.emails.map((e) => (
                  <div key={e.campaign_id || e.id} className="p-2.5 bg-[#14101c] rounded-lg" data-testid={`email-${e.campaign_id || e.id}`}>
                    <div className="text-xs text-white truncate">{e.subject || e.title || "Campagne"}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{e.status || "—"} · {e.sent_count || 0} envois</div>
                  </div>
                ))
              )}
            </RecentSection>
          </div>

          {/* Suggestions IA */}
          <div className="mt-6 bg-gradient-to-br from-violet-500/10 to-rose-500/10 border border-violet-500/30 rounded-xl p-5" data-testid="ai-suggestions">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-violet-300" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-white">Suggestions IA</h3>
                <p className="text-[11px] text-slate-300 mt-0.5">
                  L'IA peut générer pour vous : audiences, copies de posts, briefs de campagnes, A/B tests.
                </p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Link to="/admin/marketing/audiences" className="text-xs px-3 py-1.5 rounded-lg bg-violet-500 hover:bg-violet-600 text-white font-bold inline-flex items-center gap-1" data-testid="ai-suggest-audience">
                    <Plus className="w-3 h-3" /> Générer une audience
                  </Link>
                  <Link to="/admin/marketing/social" className="text-xs px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white font-bold inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Idée de post IA
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, icon, accent, testid }) {
  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-xl p-3.5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</span>
        <span style={{ color: accent }}>{icon}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1 truncate">{sub}</div>}
    </div>
  );
}

function RecentSection({ title, icon: Icon, color, href, children }) {
  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h4 className="text-xs uppercase tracking-wider text-slate-400 font-bold inline-flex items-center gap-1.5">
          <Icon className="w-3 h-3" style={{ color }} /> {title}
        </h4>
        <Link to={href} className="text-[10px] text-slate-500 hover:text-white inline-flex items-center gap-0.5">
          Voir tout <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function EmptyState({ text, cta, href }) {
  return (
    <div className="text-[11px] text-slate-500 italic p-3 text-center">
      {text}
      {cta && <Link to={href} className="block mt-2 text-violet-400 hover:underline font-bold not-italic">{cta} →</Link>}
    </div>
  );
}
